import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/pro-light-svg-icons';
import type { BankAccount } from '../../models/BankAccount';

export interface ViewVerifiedBankAccountModalRef {
  open: (account: BankAccount) => void;
  close: () => void;
}

interface ViewVerifiedBankAccountModalProps {
  /** Notas internas y “verificada por” solo para 3PL */
  showStaffFields?: boolean;
}

const formatDate = (value: string | null | undefined, locale: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

export const ViewVerifiedBankAccountModal = forwardRef<
  ViewVerifiedBankAccountModalRef,
  ViewVerifiedBankAccountModalProps
>(({ showStaffFields = false }, ref) => {
  const { t, i18n } = useTranslation('bankAccountsModule');
  const dateLocale = i18n.language?.startsWith('es') ? 'es-ES' : 'en-US';
  const modalRef = useRef<ModalCenterRef>(null);
  const [account, setAccount] = useState<BankAccount | null>(null);

  useImperativeHandle(ref, () => ({
    open: (acc: BankAccount) => {
      setAccount(acc);
      modalRef.current?.openModal();
    },
    close: () => {
      modalRef.current?.closeModal();
    },
  }));

  const openProof = () => {
    if (account?.proof_document_path) {
      window.open(account.proof_document_path, '_blank');
    }
  };

  return (
    <ModalCenter ref={modalRef} title={t('modals.viewVerified.title')} width="default">
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <div className="bg-muted flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors">
            <FontAwesomeIcon icon={faCircleCheck} className="text-primary h-5 w-5" />
          </div>
          <p className="text-sm text-muted-foreground">{t('modals.viewVerified.description')}</p>
        </div>

        {account && (
          <>
            <dl className="bg-muted space-y-3 rounded-lg p-4 text-sm">
              <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
                <dt className="shrink-0 font-medium text-foreground">{t('modals.viewVerified.fields.holder')}</dt>
                <dd className="min-w-0 break-words">{account.holder_name}</dd>
              </div>
              <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
                <dt className="shrink-0 font-medium text-foreground">{t('modals.viewVerified.fields.iban')}</dt>
                <dd className="min-w-0 break-all font-mono text-xs sm:text-sm">{account.iban}</dd>
              </div>
              <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
                <dt className="shrink-0 font-medium text-foreground">{t('modals.viewVerified.fields.bic')}</dt>
                <dd className="font-mono text-xs sm:text-sm">{account.bic}</dd>
              </div>
              <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
                <dt className="shrink-0 font-medium text-foreground">{t('modals.viewVerified.fields.bank')}</dt>
                <dd>{account.bank_name}</dd>
              </div>
              <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
                <dt className="shrink-0 font-medium text-foreground">{t('modals.viewVerified.fields.country')}</dt>
                <dd>{account.country_code}</dd>
              </div>
              <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
                <dt className="shrink-0 font-medium text-foreground">{t('modals.viewVerified.fields.team')}</dt>
                <dd>{account.team?.name ?? `#${account.team_id}`}</dd>
              </div>
              <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
                <dt className="shrink-0 font-medium text-foreground">{t('modals.viewVerified.fields.verifiedAt')}</dt>
                <dd>{formatDate(account.verified_at, dateLocale)}</dd>
              </div>
              {showStaffFields && (
                <>
                  <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
                    <dt className="shrink-0 font-medium text-foreground">
                      {t('modals.viewVerified.fields.verifiedBy')}
                    </dt>
                    <dd>
                      {account.verified_by_user?.name ??
                        (account.verified_by_user_id != null ? `#${account.verified_by_user_id}` : '—')}
                    </dd>
                  </div>
                  {account.internal_note ? (
                    <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
                      <dt className="shrink-0 font-medium text-foreground">
                        {t('modals.viewVerified.fields.internalNote')}
                      </dt>
                      <dd className="min-w-0 whitespace-pre-wrap break-words">{account.internal_note}</dd>
                    </div>
                  ) : null}
                </>
              )}
            </dl>
            {account.proof_document_path ? (
              <div className="bg-muted flex flex-col gap-3 rounded-lg p-4 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-sm font-medium text-foreground">{t('modals.viewVerified.fields.proof')}</span>
                <Button type="button" variant="outline" size="sm" onClick={openProof}>
                  {t('actions.viewDocument')}
                </Button>
              </div>
            ) : null}
          </>
        )}

        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={() => modalRef.current?.closeModal()}>
            {t('buttons.closeModal')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

ViewVerifiedBankAccountModal.displayName = 'ViewVerifiedBankAccountModal';
